import React, { useEffect, useRef, useState, useCallback } from 'react'
import axios from 'axios'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import { useTranslation } from 'react-i18next'

import { apiBase } from './api'
import {
  parseGatewayMessage,
  isSessionStale,
  isPaid,
  originOf,
  SESSION_TTL_MS,
  CONFIRM_POLL_MS,
  CONFIRM_TIMEOUT_MS,
  BACKGROUND_POLL_MS,
  IFRAME_WATCHDOG_MS,
} from './gateway'

/**
 * The card gateway's iframe, and everything that can go wrong around it.
 *
 * What this replaces: an `<iframe>` whose src came from the page load, a `message` listener
 * that believed any origin, `onMessage` (not a real React DOM prop -- it never fired), a
 * `console.log` of the payment payload, and a step advance on the gateway's word alone.
 *
 * The states, in the order a payer meets them:
 *
 *   minting    -> asking the server for a gateway session (spinner, not a blank box)
 *   ready      -> the frame is up; a watchdog is running until it loads
 *   confirming -> the card was approved; asking OUR server whether the document exists
 *   failed     -> declined, or the frame never loaded, or the session could not be minted
 *   expired    -> the session outlived its TTL with nothing submitted
 *
 * Nothing but `parseGatewayMessage` and the state endpoint advances any of it.
 */

const PANEL = { textAlign: 'center', padding: '32px 16px' }

export default function PaymentForm({ data, onPaid }) {
  const { t } = useTranslation()

  const [phase, setPhase] = useState('minting')
  const [iframeUrl, setIframeUrl] = useState(null)
  const [mintedAt, setMintedAt] = useState(null)

  const hash = data.hash_id

  // From the server. Falling back to the API host keeps a page working against a backend
  // that predates message_origin; '' would make parseGatewayMessage reject everything.
  const allowedOrigin = data.message_origin || originOf(apiBase())

  // Survives re-renders and, more importantly, lets every async path check whether the
  // component is still mounted before calling setState on it.
  const alive = useRef(true)
  useEffect(() => () => { alive.current = false }, [])

  /**
   * Ask our server whether the document exists.
   *
   * Returns true once it does, so both callers -- the fast confirm poll and the slow
   * background one -- can stop.
   */
  const checkState = useCallback(async () => {
    try {
      const res = await axios.get(apiBase() + '/paymentreq/state/' + hash)

      if (alive.current && isPaid(res.data)) {
        onPaid(res.data)
        return true
      }
    } catch (e) {
      // A poll that fails is a poll; the next one tries again. The payer is told nothing,
      // because nothing has gone wrong from where they are sitting.
    }

    return false
  }, [hash, onPaid])

  const mint = useCallback(async () => {
    setPhase('minting')
    setIframeUrl(null)

    try {
      const res = await axios.post(apiBase() + '/paymentreq/iframe/' + hash)

      if (!alive.current) return

      if (res.data && res.data.iframe_url) {
        setIframeUrl(res.data.iframe_url)
        setMintedAt(Date.now())
        setPhase('ready')
      } else {
        setPhase('failed')
      }
    } catch (e) {
      if (!alive.current) return

      // 409 is "this request is already paid" -- reached by reopening a link after paying,
      // or by paying in a second tab. Showing "payment unavailable" there would tell someone
      // who HAS paid that they have not, so ask the server and show them their receipt.
      if (e.response && e.response.status === 409) {
        setPhase('confirming')
        return
      }

      setPhase('failed')
    }
  }, [hash])

  useEffect(() => { mint() }, [mint])

  /**
   * The fallback for a '000' that never arrives -- a gateway that opened its own window, a
   * blocked postMessage, an origin that does not match. Slow, because it is a backstop.
   */
  useEffect(() => {
    if (phase !== 'ready') return undefined

    const id = setInterval(checkState, BACKGROUND_POLL_MS)
    return () => clearInterval(id)
  }, [phase, checkState])

  /** The gateway's word. It starts the confirmation; it does not finish it. */
  useEffect(() => {
    const onMessage = (event) => {
      const outcome = parseGatewayMessage(event, allowedOrigin, hash)

      if (outcome === 'success') {
        setPhase('confirming')
      } else if (outcome === 'failure') {
        // A declined card, not a broken page. The gateway's own error is inside the frame
        // with no way out of it, so this is the way back -- and retrying has to start from
        // the details step, because a declined attempt's session is spent.
        setPhase('declined')
      }
    }

    window.addEventListener('message', onMessage, false)
    return () => window.removeEventListener('message', onMessage, false)
  }, [allowedOrigin, hash])

  /**
   * Confirming: poll fast, and give up after 30s into a reassuring message rather than a
   * failure. The money is gone by this point; telling the payer it failed starts a support
   * call and, worse, invites a second payment.
   */
  useEffect(() => {
    if (phase !== 'confirming') return undefined

    let stopped = false
    const started = Date.now()

    const tick = async () => {
      if (stopped) return

      if (await checkState()) return

      if (stopped) return

      if (Date.now() - started > CONFIRM_TIMEOUT_MS) {
        onPaid({ status: null, docnum: null, emailed: false, pending: true })
        return
      }

      setTimeout(tick, CONFIRM_POLL_MS)
    }

    tick()
    return () => { stopped = true }
  }, [phase, checkState, onPaid])

  /** The session outlives its usefulness before the gateway admits it. */
  useEffect(() => {
    if (phase !== 'ready' || !mintedAt) return undefined

    const id = setTimeout(() => {
      if (alive.current && isSessionStale(mintedAt, Date.now())) setPhase('expired')
    }, SESSION_TTL_MS)

    return () => clearTimeout(id)
  }, [phase, mintedAt])

  /**
   * A frame that never loads is indistinguishable from one that is still loading, and the
   * payer stares at a white box. onLoad clears this.
   */
  const watchdog = useRef(null)
  useEffect(() => {
    if (phase !== 'ready') return undefined

    watchdog.current = setTimeout(() => {
      if (alive.current) setPhase('failed')
    }, IFRAME_WATCHDOG_MS)

    return () => clearTimeout(watchdog.current)
  }, [phase, iframeUrl])

  const onFrameLoad = () => clearTimeout(watchdog.current)

  if (phase === 'minting') {
    return (
      <div style={PANEL}>
        <CircularProgress />
        <Typography variant="body1" style={{ marginTop: 16 }}>
          {t('OpeningPaymentForm')}
        </Typography>
      </div>
    )
  }

  if (phase === 'confirming') {
    return (
      <div style={PANEL}>
        <CircularProgress />
        <Typography variant="h6" style={{ marginTop: 16 }}>
          {t('ConfirmingYourPayment')}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {t('DoNotCloseThisPage')}
        </Typography>
      </div>
    )
  }

  if (phase === 'declined') {
    return (
      <div style={PANEL}>
        <Typography variant="h6" gutterBottom>{t('PaymentFailedRetry')}</Typography>
        {/* Mints a NEW session. The throttle allows 30 an hour, so a payer trying another
            card is never the one it stops. */}
        <Button variant="contained" color="primary" onClick={mint} style={{ marginTop: 16 }}>
          {t('Retry')}
        </Button>
      </div>
    )
  }

  if (phase === 'expired') {
    return (
      <div style={PANEL}>
        <Typography variant="h6" gutterBottom>{t('PaymentFormExpired')}</Typography>
        <Typography variant="body1" gutterBottom>{t('PaymentFormExpiredDetail')}</Typography>
        <Button variant="contained" color="primary" onClick={mint} style={{ marginTop: 16 }}>
          {t('StartAgain')}
        </Button>
      </div>
    )
  }

  if (phase === 'failed' || !iframeUrl) {
    return (
      <div style={PANEL}>
        <Typography variant="h6" gutterBottom>{t('PaymentUnavailable')}</Typography>
        <Typography variant="body1" gutterBottom>{t('PaymentUnavailableTryLater')}</Typography>
        {/* Retry mints a NEW session. Reusing the old one is what produced a frame the
            gateway had already closed. */}
        <Button variant="contained" color="primary" onClick={mint} style={{ marginTop: 16 }}>
          {t('Retry')}
        </Button>
      </div>
    )
  }

  return (
    <iframe
      title={t('PaymentDetails')}
      src={iframeUrl}
      onLoad={onFrameLoad}
      width="100%"
      /* I7: a fixed 575px cut the bottom off the taller gateway forms on a phone, and the
         frame does not scroll the page it is in. */
      style={{ height: 'max(420px, min(720px, 100vh - 220px))', border: 0, display: 'block' }}
      frameBorder="0"
      scrolling="auto"
      allow="payment"
    />
  )
}
